import { observer } from 'mobx-react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Layout, useUserStore } from '@roc/feature-app-core';
import {
  Box,
  Grid,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { AppraiserGrid } from './appraiserGrid';
import { useAppraiserStore } from '../hooks/useAppraiserStore';
import {
  Button,
  GridColumnProps,
  PDFPreview,
  StandardDialog,
  Toolbar,
} from '@roc/ui';
import { filterProps, noOpComparator, selectFilterProps } from '@roc/ui/utils';
import { KeyboardArrowRight, Send } from '@material-ui/icons';
import { AddEditAppraiser } from './addEditAppraiser';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    //Same as nportal dashboard
    maxWidth: '1400px',
  },
}));

export const AppraiserDashboard = observer(() => {
  const classes = useStyles();

  const { appraiserStore } = useAppraiserStore();
  const { userStore } = useUserStore();

  useEffect(() => {
    const loadUserInfo = async () => {
      if (appraiserStore.globalStore.isInternalPortal) {
        const userInfo = await userStore.getUserInformation();
        userStore.setInternalUser(userInfo.user, null);
      }
    };
    loadUserInfo();
  }, [userStore]);

  const actions = (record, handleMenuClose) => {
    const actionsRowMenu = [
      {
        icon: KeyboardArrowRight,
        text: 'Edit',
        onClickAction: () => {
          appraiserStore.setAppraiser(record);
        },
      },
    ];

    return (
      <>
        {actionsRowMenu.map(item => (
          <MenuItem
            key={`menu_${item.text}`}
            onClick={() => {
              item.onClickAction();
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <item.icon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{item.text}</ListItemText>
          </MenuItem>
        ))}
      </>
    );
  };

  const columns: GridColumnProps[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 100,
      cellRenderer: 'appraiserNameCellRenderer',
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'firm',
      headerName: 'AMC',
      width: 100,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 120,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 90,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 80,
      cellRenderer: 'statusCellRenderer',
      comparator: noOpComparator,
      ...selectFilterProps(appraiserStore.statusOptions),
    },
    {
      headerName: 'Actions',
      width: 90,
      minWidth: 90,
      cellRenderer: 'actionCellRenderer',
      comparator: noOpComparator,
      pinned: 'right',
      lockPinned: true,
    },
  ];

  return (
    <Layout containerClass={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12} style={{ margin: '0 0 30px 0' }}>
          <Box mb={1}>
            <Typography variant="h4">
              {appraiserStore.currentAppraiser ? 'Appraiser Information' : 'Appraisers'}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <AppraiserGrid
            store={appraiserStore}
            columns={columns}
            actions={actions}
            toolbar={
              userStore.isTechUser && (
                <Grid container>
                  <Grid item xs={12}>
                    <Toolbar>
                      <Button
                        variant="contained"
                        color="primary"
                        testId="add-appraiser"
                        onClick={() => {
                          appraiserStore.setAppraiser(null);
                          appraiserStore.appraiserFormStore.reset();
                          appraiserStore.addEditOpen = true;
                        }}
                      >
                        Add Appraiser
                      </Button>
                    </Toolbar>
                  </Grid>
                </Grid>
              )
            }
          />
        </Grid>
      </Grid>
      <StandardDialog
        open={appraiserStore.addEditOpen}
        handleClose={() => {
          appraiserStore.setAppraiser(null);
          appraiserStore.addEditOpen = false;
        }}
        dialogContent={<AddEditAppraiser store={appraiserStore} />}
        dialogActions={
          <>
            <Button
              style={{ marginRight: '8px' }}
              onClick={() => {
                appraiserStore.setAppraiser(null);
                appraiserStore.addEditOpen = false;
              }}
              color="primary"
              variant="outlined"
              testId="cancel"
            >
              Cancel
            </Button>
            <Button
              testId={'save'}
              onClick={() => {
                appraiserStore.saveAppraiser();
              }}
              variant="contained"
              type="submit"
              color="primary"
              disabled={!(userStore.isTechUser || userStore.isCommittee)}
            >
              Save
            </Button>
          </>
        }
      />
      <PDFPreview
        open={appraiserStore.isPdfPreviewModalOpen}
        title={appraiserStore.pdfTitle}
        data={appraiserStore.pdfData}
        headers={{}}
        fileName={appraiserStore.pdfTitle}
        onClose={() => {
          appraiserStore.pdfTitle = null;
          appraiserStore.pdfData = null;
          appraiserStore.isPdfPreviewModalOpen = false;
        }}
      />
    </Layout>
  );
});
